import { Permission, byRank, has } from "@entities/role";
import { SEPARATOR, type MenuItem } from "@shared/ui/Menu";
import type { Relation, Role, Uuid } from "@shared/api";

/** Сроки тайм-аута — те же, что предлагает Discord. */
const TIMEOUTS = [
  { label: "60 секунд", minutes: 1 },
  { label: "5 минут", minutes: 5 },
  { label: "10 минут", minutes: 10 },
  { label: "1 час", minutes: 60 },
  { label: "1 день", minutes: 1440 },
  { label: "1 неделя", minutes: 10080 },
];

/** Мои права на сервере — как их отдаёт `permissions/current`. */
export interface Rights {
  permissions: number;
  rank: number;
  is_owner: boolean;
}

/**
 * Роли, которые я вправе выдать и снять: с правом выдавать роли и только
 * младше своей старшей, владельцу — все. Роль по умолчанию есть у всех, её не
 * выдают. Остальное решает сервер.
 */
export function assignableRoles(roles: readonly Role[], rights: Rights | null): Role[] {
  const owner = !!rights?.is_owner;
  if (!owner && !(rights && has(rights.permissions, Permission.AssignRoles))) return [];
  return byRank(roles).filter((role) => !role.is_default && (owner || (!!rights && role.rank < rights.rank)));
}

/**
 * Меню участника по правому щелчку — набор из Discord, переложенный на наш
 * сервер: профиль и личная переписка, дружба и блок, роли, выгнать и бан.
 *
 * Пункты модерации появляются только с правом на них. Старшинство здесь
 * отсеивает лишь роли, которые выдать нельзя заведомо (свою и старше), —
 * остальное решает сервер, и его отказ показывают окна действий.
 */
export function memberMenu({
  self,
  name,
  handle,
  relation,
  memberRoles,
  roles,
  rights,
  timedOut = false,
  apps = [],
  actions,
}: {
  self: boolean;
  name: string;
  handle: string;
  relation: Relation;
  /** Роли участника на этом сервере. */
  memberRoles: readonly Uuid[];
  roles: readonly Role[];
  rights: Rights | null;
  /** Участник сейчас в тайм-ауте: пункт его снимает. */
  timedOut?: boolean;
  /** Команды ботов на человека — подменю «Приложения», как в Discord. */
  apps?: MenuItem[];
  actions: {
    profile: () => void;
    write: () => void;
    friend: (add: boolean) => void;
    block: (block: boolean) => void;
    role: (roleId: Uuid, on: boolean) => void;
    kick: () => void;
    ban: () => void;
    /** Тайм-аут на столько минут; `null` — снять. */
    timeout?: (minutes: number | null) => void;
    /** Сменить ник на сервере. */
    nickname?: () => void;
    /** «Пригласить на сервер» — куда звать, кроме этого сервера, и как отправить. */
    invite?: { servers: readonly { id: Uuid; name: string }[]; send: (serverId: Uuid) => void };
  };
}): MenuItem[] {
  const items: MenuItem[] = [{ label: "Профиль", onPick: actions.profile }];
  if (!self) items.push({ label: "Написать лично", onPick: actions.write });
  // Как в Discord: приглашение уходит человеку в личку.
  const invite = actions.invite;
  if (!self && invite && invite.servers.length > 0) {
    items.push({
      label: "Пригласить на сервер",
      items: invite.servers.map((server) => ({ label: server.name, onPick: () => invite.send(server.id) })),
    });
  }
  if (apps.length) items.push({ label: "Приложения", items: apps });

  // Ник — как в Discord: свой меняет каждый, у кого право (оно у всех по
  // умолчанию), чужой — с правом менять ники; старшинство проверит сервер.
  const owner = !!rights?.is_owner;
  const can = (bit: bigint) => owner || (!!rights && has(rights.permissions, bit));
  if (
    actions.nickname &&
    (self ? can(Permission.ChangeNickname) || can(Permission.ManageNicknames) : can(Permission.ManageNicknames))
  ) {
    items.push({ label: self ? "Изменить ник на сервере" : "Изменить ник", onPick: actions.nickname });
  }

  if (!self) {
    const social: MenuItem[] = [];
    if (relation === "friend") social.push({ label: "Удалить из друзей", onPick: () => actions.friend(false) });
    else if (relation === "outgoing") social.push({ label: "Отменить заявку в друзья", onPick: () => actions.friend(false) });
    else if (relation === "incoming") social.push({ label: "Принять заявку в друзья", onPick: () => actions.friend(true) });
    else if (relation === "none") social.push({ label: "Добавить в друзья", onPick: () => actions.friend(true) });
    social.push(
      relation === "blocked"
        ? { label: "Разблокировать", onPick: () => actions.block(false) }
        : { label: "Заблокировать", kind: "danger", onPick: () => actions.block(true) },
    );
    items.push(SEPARATOR, ...social);
  }

  const assignable = assignableRoles(roles, rights);
  if (assignable.length) {
    items.push(SEPARATOR, {
      label: "Роли",
      items: assignable.map((role) => {
        const on = memberRoles.includes(role.id);
        return { label: role.name, choice: true, on, onPick: () => actions.role(role.id, !on) };
      }),
    });
  }

  if (!self) {
    const harsh: MenuItem[] = [];
    // Тайм-аут — как в Discord: сроки от минуты до недели, снять — отдельным пунктом.
    if (can(Permission.TimeoutMembers) && actions.timeout) {
      const timeout = actions.timeout;
      harsh.push(
        timedOut
          ? { label: "Снять тайм-аут", onPick: () => timeout(null) }
          : {
              label: `Тайм-аут ${name}`,
              kind: "danger",
              items: TIMEOUTS.map((term) => ({ label: term.label, onPick: () => timeout(term.minutes) })),
            },
      );
    }
    if (can(Permission.KickMembers)) harsh.push({ label: `Выгнать ${name}`, kind: "danger", onPick: actions.kick });
    if (can(Permission.BanMembers)) harsh.push({ label: `Забанить ${name}`, kind: "danger", onPick: actions.ban });
    if (harsh.length) items.push(SEPARATOR, ...harsh);
  }

  items.push(SEPARATOR, {
    label: "Копировать хэндл",
    hint: handle,
    onPick: () => void navigator.clipboard?.writeText(handle).catch(() => {}),
  });
  return items;
}
